import { usePlatform } from "@/context/platform"
import type { ServerConnection } from "@/context/server"
import { requestHealth } from "@/transport/health"
import { defaultTransportFetch, type TransportFetch } from "@/transport/http"
import { consumeHealthyRoute, healthCheckReuseMs } from "@/transport/health-probe-cache"

export type ServerHealth = { healthy: boolean; version?: string }

interface CheckServerHealthOptions {
  timeoutMs?: number
  signal?: AbortSignal
}

const defaultTimeoutMs = 3000
const sharedHealthChecks = new Map<string, { expiresAt: number; promise: Promise<ServerHealth> }>()

export async function checkServerHealth(
  server: ServerConnection.HttpBase,
  fetch: TransportFetch,
  opts?: CheckServerHealthOptions,
): Promise<ServerHealth> {
  if (opts?.signal?.aborted) return { healthy: false }
  if (consumeHealthyRoute(server)) return { healthy: true }
  const controller = new AbortController()
  const abort = () => controller.abort(opts?.signal?.reason)
  opts?.signal?.addEventListener("abort", abort, { once: true })
  // Leave time for explicit route recovery inside the connection owner's deadline.
  const timer = setTimeout(() => controller.abort(new DOMException("Health check timed out", "TimeoutError")), opts?.timeoutMs ?? defaultTimeoutMs)
  try {
    const result = await requestHealth(server.url, { signal: controller.signal, fetch, expectedMachineName: server.expectedMachineName })
    return result.healthy ? result : { healthy: false }
  } finally {
    clearTimeout(timer)
    opts?.signal?.removeEventListener("abort", abort)
  }
}

export function useCheckServerHealth() {
  const platform = usePlatform()
  const fetcher = platform.fetch ?? defaultTransportFetch()

  return (http: ServerConnection.HttpBase, opts?: CheckServerHealthOptions) => {
    if (opts?.signal) return checkServerHealth(http, fetcher, opts)
    const key = JSON.stringify([http.url, http.expectedMachineName ?? ""])
    const cached = sharedHealthChecks.get(key)
    if (cached && cached.expiresAt > Date.now()) return cached.promise
    const promise = checkServerHealth(http, fetcher)
    sharedHealthChecks.set(key, { expiresAt: Date.now() + healthCheckReuseMs, promise })
    const release = () => {
      setTimeout(() => {
        if (sharedHealthChecks.get(key)?.promise === promise) sharedHealthChecks.delete(key)
      }, 1000)
    }
    void promise.then(release, release)
    return promise
  }
}
